import _ from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApiCallAction } from './utils';
import { makeApiCall } from '../../utils/makeApiCall';
import { Team, TeamUser } from '../../interfaces/team';
import { setCurrentTeam } from '../Slices/teamSlice';
import {
  resetOrganization,
  setAllCredentials,
  setOrganization,
} from '../Slices/organizationSlice';
import { getSubscriptionStatus } from './subscriptionActionTypes';
import { fetchCreditBalance } from './creditBalanceActionTypes';
import { fetchCredentials } from '../../services/organizationService';
import { resetChangeRequest } from '../Slices/changeRequestSlice';
import { resetChangeRequests } from '../Slices/ChangeRequestItemsSlice';
import { resetAdminAgent } from '../Slices/adminAgentSlice';
import { resetChangeRequestEdit } from '../Slices/changeRequestEditSlice';
import { resetChangeRequestSteps } from '../Slices/changeRequestStepsSlice';
import { resetCreditBalance } from '../Slices/creditBalance';
import { resetPromptLibrary } from '../Slices/promptLibrarySlice';
import { resetSubscription } from '../Slices/subscriptionSlice';
import { setGlobalState } from '../Slices/globalSlice';
import { PersonalUser } from '../../interfaces/account';
import { Notify } from '../../services/notificationService';
import { serializeError } from '../../utils/errorHandling';

export const _fetchAllTeams = createApiCallAction('team/fetchAllTeams', {
  url: '/organizations?with_current_user=true',
  method: 'GET',
});

const _fetchPersonalAccount = createApiCallAction('team/fetchPersonalAccount', {
  url: '/accounts/users/me',
  method: 'GET',
});

const _convertUndefinedFromBackend = (obj: unknown): unknown => {
  if (_.isArray(obj)) {
    return _.map(obj, _convertUndefinedFromBackend);
  }
  if (_.isPlainObject(obj)) {
    return _.mapValues(
      obj as Record<string, unknown>,
      _convertUndefinedFromBackend
    );
  }
  return obj === '__UNDEFINED__' ? undefined : obj;
};

const _fetchAbility = createAsyncThunk(
  'team/fetchAbilities',
  async (
    { account }: { account: PersonalUser | TeamUser },
    { rejectWithValue }
  ) => {
    try {
      // Validate account is a valid object before using 'in' operator
      if (!account || typeof account !== 'object') {
        console.error('_fetchAbility called with invalid account:', {
          account,
          accountType: typeof account,
        });
        throw new Error('Invalid account object provided to _fetchAbility');
      }

      const headers =
        'cirra_organization_id' in account
          ? { cirra_organization_id: account.cirra_organization_id }
          : {};
      const res = await makeApiCall({
        url: '/accounts/users/me/ability',
        method: 'GET',
        headers: headers as unknown as Record<string, string>,
      });

      if (!res.data) {
        return [];
      }

      const abilityRules = _convertUndefinedFromBackend(res.data);
      return abilityRules;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const fetchAllAccounts = createAsyncThunk(
  'team/fetchAllAccounts',
  async (__, { dispatch, rejectWithValue }) => {
    try {
      const [personalAccountRes, allTeamsRes] = await Promise.all([
        dispatch(_fetchPersonalAccount()).unwrap(),
        dispatch(_fetchAllTeams()).unwrap(),
      ]);

      const personalAccount = personalAccountRes.data as PersonalUser;
      const allTeams = _convertUndefinedFromBackend(allTeamsRes.data); // already has ability attached

      // Validate personal account data
      if (!personalAccount) {
        throw new Error('Failed to retrieve personal account data');
      }

      // Attach personal ability to personal account
      try {
        const personalAbility = await dispatch(
          _fetchAbility({ account: personalAccount })
        ).unwrap();
        personalAccount.ability = personalAbility as Record<string, unknown>[];
      } catch (error) {
        const errorPayload = error as {
          message?: string;
          statusCode?: number;
          errorCode?: string;
        };
        console.error('Failed to load personal permissions:', {
          account: personalAccount,
          statusCode: errorPayload.statusCode,
          errorCode: errorPayload.errorCode,
          message: errorPayload.message,
        });
        Notify.error(
          `Failed to load permissions${errorPayload.statusCode ? ` (${errorPayload.statusCode})` : ''}: ${errorPayload.message || 'Unknown error'}`
        );
        personalAccount.ability = [];
      }

      return {
        personalAccount,
        allTeams,
      };
    } catch (error) {
      // Let the caller (AuthenticationGuard) handle retries with exponential backoff
      // Don't reload automatically as it causes infinite loops when backend is down
      return rejectWithValue(serializeError(error));
    }
  }
);

export const fetchInitialData = createAsyncThunk(
  'team/fetchInitialData',
  async (__, { dispatch, rejectWithValue }) => {
    try {
      dispatch(resetSubscription());
      dispatch(resetOrganization());
      dispatch(resetCreditBalance());
      dispatch(resetChangeRequests());
      dispatch(resetChangeRequest());
      dispatch(resetAdminAgent());
      dispatch(resetChangeRequestEdit());
      dispatch(resetChangeRequestSteps());
      dispatch(resetPromptLibrary());

      const [, , credentials] = await Promise.all([
        dispatch(getSubscriptionStatus()),
        dispatch(fetchCreditBalance()),
        fetchCredentials(),
      ]);
      dispatch(setAllCredentials(credentials));
    } catch (error) {
      console.log('[fetchInitialData] Caught error:', error);
      const serialized = serializeError(error);
      console.log('[fetchInitialData] Serialized error:', serialized);
      return rejectWithValue(serialized);
    }
  }
);

export const selectAccount = createAsyncThunk(
  'team/selectAccount',
  async (team: Team | null, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setGlobalState({ loading: true }));

      // Immediately clear organization data to prevent showing stale data
      dispatch(setOrganization(null));
      dispatch(setAllCredentials([]));

      // FIRST: Update Redux state synchronously
      dispatch(setCurrentTeam(team));

      // SECOND: Update backend with new team context
      await makeApiCall({
        url: `/accounts/users/me/current-organization`,
        method: 'PATCH',
        data: {
          cirra_organization_id: team?.cirra_organization_id || null,
        },
      });

      // THIRD: Fetch all data with the new team context (now properly set in Redux)
      await dispatch(fetchInitialData());
    } catch (error) {
      return rejectWithValue(serializeError(error));
    } finally {
      dispatch(setGlobalState({ loading: false }));
    }
  }
);

type CreateTeamPayload = {
  name: string;
  master_organization_id?: string | null;
};

export const createTeam = createAsyncThunk(
  'team/createNew',
  async (payload: CreateTeamPayload, { dispatch, rejectWithValue }) => {
    try {
      const res = await makeApiCall({
        url: '/organizations',
        method: 'POST',
        data: payload,
      });
      const team = res.data;

      // Validate team data
      if (!team || !team.current_user) {
        throw new Error('Failed to retrieve team data from server');
      }

      // Attach ability to the new team's owner account
      try {
        const ability = await dispatch(
          _fetchAbility({ account: team.current_user })
        ).unwrap();
        team.current_user.ability = ability as Record<string, unknown>[];
      } catch (error) {
        const errorPayload = error as {
          message?: string;
          statusCode?: number;
          errorCode?: string;
        };
        console.error('Failed to load team permissions:', {
          team: team,
          currentUser: team.current_user,
          statusCode: errorPayload.statusCode,
          errorCode: errorPayload.errorCode,
          message: errorPayload.message,
        });
        Notify.error(
          `Failed to load permissions${errorPayload.statusCode ? ` (${errorPayload.statusCode})` : ''}: ${errorPayload.message || 'Unknown error'}`
        );
        team.current_user.ability = [];
      }

      res.data = team;
      return res;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

type DeleteTeamPayload = {
  team: Team;
};

export const deleteTeam = createAsyncThunk(
  'team/delete',
  async (payload: DeleteTeamPayload, { rejectWithValue }) => {
    try {
      const { team } = payload;
      return await makeApiCall({
        url: `/organizations/${team.id}`,
        method: 'DELETE',
        asUser: team.current_user,
      });
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

type RenameTeamPayload = {
  team: Team;
  newName: string;
};

export const renameTeam = createAsyncThunk(
  'team/rename',
  async (payload: RenameTeamPayload, { rejectWithValue }) => {
    try {
      const { team, newName } = payload;
      return await makeApiCall({
        url: `/organizations/${team.id}`,
        method: 'PATCH',
        data: {
          cirra_organization_name: newName,
        },
        asUser: team.current_user,
      });
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);
